import {CHAT_PERSONAS, ChatPersonaType} from "../../toktiv-common/types";
import {ChatMessage, MessageFactory, ParticipantKey, ParticipantType} from "../shared/messages/chat/types";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../../shared/BaseFragment";
import React, {ReactElement} from "react";
import {App} from "../App";
import {ChatAgent} from "./ChatAgent";
import {colorRed} from "../../shared/colors";
import {StyledBoxColumn, StyledBoxRow, StyledDurationView} from "../../shared/StyledComponents";
import {PD_LG, PD_MD, PD_SM, PD_XLG} from "../../shared/dimens";
import {Button, CircularProgress, IconButton, Typography} from "@mui/material";
import {CallEndOutlined} from "@mui/icons-material";
import {getMemberAuth} from "../../shared/auth";
import {RecordAudioDisplayMode, RecordAudioFragment} from "../../shared/RecordAudioFragment";
import {ClockGranularity} from "../../shared/clock";
import {AppPrefs} from "../app_prefs";
import {VISEMES} from "wawa-lipsync";
import {FaceModelMouth} from "./types";
import {Avatar} from "./Avatar";

export type AppVideoCallFragmentProps = BaseFragmentProps & {
  personaType: ChatPersonaType,
  myParticipantKey: ParticipantKey,
  onBackPressed: () => boolean,
}

export type AppVideoCallFragmentState = BaseFragmentState & {
  agentBusy?: boolean,
  agentText?: string,
  audioStatus?: ReactElement,
  audioPlaying?: boolean,
  callStartedAt: number,
  messages: ChatMessage[],
  audioSrc?: string,
}

const VISMEME_MAP = {
  "sil": {"open": 0.28, "upperLip": 0.06, "lowerLip": 0.08, "rounded": 0.5, "smiling": 0, "skew": 0, "rotate": 0},
  "PP": {"open": 0.21, "upperLip": 0.3, "lowerLip": 0.32, "rounded": 0.5, "smiling": 0.66, "skew": 0, "rotate": 0},
  "FF": {"open": 0.07, "upperLip": 0, "lowerLip": 0.31, "rounded": 0.5, "smiling": 0, "skew": 0, "rotate": 0},
  "TH": {"open": 0.25, "upperLip": 0, "lowerLip": 0.28, "rounded": 0.52, "smiling": 0.31, "skew": 0, "rotate": 0},
  "DD": {"open": 0.25, "upperLip": 0.33, "lowerLip": 0.27, "rounded": 0.5, "smiling": 0, "skew": 0, "rotate": 0},
  "kk": {"open": 0.05, "upperLip": 0.16, "lowerLip": 0.6, "rounded": 0.5, "smiling": 0, "skew": 0, "rotate": 0},
  "CH": {"open": 0.4, "upperLip": 0, "lowerLip": 0.6, "rounded": 0.5, "smiling": 0, "skew": 0, "rotate": 0},
  "SS": {"open": 0.25, "upperLip": 0.4, "lowerLip": 0.93, "rounded": 0.5, "smiling": 0, "skew": 0, "rotate": 0},
  "nn": {"open": 0.03, "upperLip": 0.35, "lowerLip": 0.6, "rounded": 0.5, "smiling": 0, "skew": 0, "rotate": 0},
  "RR": {"open": 0.25, "upperLip": 0, "lowerLip": 0.34, "rounded": 0.5, "smiling": 0.32, "skew": 0, "rotate": 0},
  "aa": {"open": 0.2, "upperLip": 0, "lowerLip": 0.6, "rounded": 0.5, "smiling": 0.27, "skew": 0, "rotate": 0},
  "E": {"open": 0, "upperLip": 0.25, "lowerLip": 0.3, "rounded": 0.5, "smiling": 0, "skew": 0, "rotate": 0},
  "I": {"open": 0.12, "upperLip": 0, "lowerLip": 0.6, "rounded": 0.5, "smiling": 0, "skew": 0, "rotate": 0},
  "O": {"open": 0.16, "upperLip": 0, "lowerLip": 0.6, "rounded": 0.5, "smiling": 0.74, "skew": 0, "rotate": 0},
  "U": {"open": 0.22, "upperLip": 0, "lowerLip": 0.6, "rounded": 0.5, "smiling": 0, "skew": 0, "rotate": 0}
};

export class AppVideoCallFragment extends BaseFragment<AppVideoCallFragmentProps, AppVideoCallFragmentState> {

  private readonly memberAuth = getMemberAuth();
  private readonly audio = App.getAppRefs().audio;
  private readonly agent: ChatAgent = new ChatAgent(this.props.myParticipantKey);
  private readonly appPrefs = AppPrefs.getInstance();
  private readonly visemeMap = new Map<VISEMES, FaceModelMouth>();

  constructor(props: AppVideoCallFragmentProps, context: any) {
    super(props, context);
    Object.getOwnPropertyNames(VISEMES).forEach(name => {
      const viseme = VISEMES[name];
      this.visemeMap.set(viseme, VISMEME_MAP[name] as FaceModelMouth);
    });
  }

  protected onCreateState(): AppVideoCallFragmentState {
    return {
      ...super.onCreateState(),
      callStartedAt: Date.now(),
      messages: [],
    };
  }

  componentDidUpdate(prevProps: Readonly<AppVideoCallFragmentProps>, prevState: Readonly<AppVideoCallFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.audioSrc !== this.state.audioSrc) {
      setTimeout(() => {
        this.audio.play();
      }, 100);
    }
    if (prevState.audioPlaying !== this.state.audioPlaying) {
      if (prevState.audioPlaying && !this.state.audioPlaying) {
        this.setState({
          agentBusy: false,
        });
      }
      this.setState({
        audioStatus: this.state.audioPlaying
          ? <StyledBoxRow style={{padding: PD_MD, alignItems: "center", justifyContent: "center"}}>
            <Typography>Speaking...</Typography>
            <Button
              variant="contained"
              onClick={() => this.audio.pause()}>
              Stop
            </Button>
          </StyledBoxRow>
          : null,
      })
    }
  }

  private async playText(text: string) {
    const encodedFile = await this.agent.textToSpeech(this.props.personaType, text);
    const mimetype = "data:audio/mp3";
    const byteString = atob(encodedFile);

    // Create an ArrayBuffer and a Uint8Array to store the binary data
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    // Populate the Uint8Array with the binary data
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    // Create and return the Blob object
    const blob = new Blob([ab], {type: mimetype});
    // const audioSrc = mimetype + ";base64," + encodedFile;
    const audioSrc = URL.createObjectURL(blob);
    this.audio.src = audioSrc;
    this.setState({audioSrc});
  }

  componentDidMount() {
    super.componentDidMount();
    this.audio.addEventListener("play", this.onPlayStateChanged.bind(this));
    this.audio.addEventListener("pause", this.onPlayStateChanged.bind(this));
    this.audio.addEventListener("ended", this.onPlayStateChanged.bind(this));
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.audio.removeEventListener("play", this.onPlayStateChanged.bind(this));
    this.audio.removeEventListener("pause", this.onPlayStateChanged.bind(this));
    this.audio.removeEventListener("ended", this.onPlayStateChanged.bind(this));
  }

  onPlayStateChanged(): void {
    this.setState({
      audioPlaying: !this.audio.paused,
    });
  }

  private async respond(url: string): Promise<void> {
    this.setState({
      agentBusy: true,
      agentText: null,
    });
    const text = await this.agent.speechToText(url);
    let messages = [...this.state.messages, MessageFactory.createTextMessage(null, this.props.myParticipantKey, text)];
    const response = await this.agent.chat(this.props.personaType, messages);
    await this.playText(response);
    messages = [...messages, MessageFactory.createTextMessage(null, new ParticipantKey(this.props.personaType, ParticipantType.MEMBER), text)];
    this.setState({
      agentText: response,
      messages: messages,
    });
  }

  protected renderContainerContent(): React.ReactElement | null {
    const persona = CHAT_PERSONAS.find(persona => persona.type === this.props.personaType);
    return <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        padding: PD_LG,
        backgroundImage: "linear-gradient(315deg, #eec0c6 0%, #7ee8fa 74%)",
        // background: nightGray,
      }}>
      <StyledBoxColumn style={{padding: PD_LG, flexGrow: 1}}>
        <StyledDurationView startedAt={this.state.callStartedAt} granularity={ClockGranularity.SECOND}
                            renderDuration={durationMs => {
                              let t = Math.floor(durationMs / 1000);
                              const sec = t % 60;
                              const min = Math.floor(t /= 60) % 60;
                              const hour = Math.floor(t /= 60);
                              return (hour > 0 ? (hour + ":") : "") + min.toString().padStart(2, "0") + ":" + sec.toString().padStart(2, "0");
                            }}/>
        <StyledBoxColumn style={{
          gap: PD_XLG,
          flexBasis: 0,
          flexGrow: 1,
          padding: PD_LG,
          alignItems: "center",
        }}>
          <StyledBoxColumn style={{
            aspectRatio: 1,
            marginTop: 48,
            filter: !this.state.agentBusy ? "grayscale(1)" : null,
            opacity: !this.state.agentBusy ? 0.5 : null,
          }}>
            <Avatar
              personaType={this.props.personaType}
              visemeMap={this.visemeMap}
              size={240}
              audio={this.audio}
              src={this.state.audioSrc}
            />
          </StyledBoxColumn>
          <Typography variant="h6">{persona.displayName}</Typography>
          <StyledBoxRow style={{alignItems: "center", justifyContent: "center", height: 48}}>
            {!this.state.agentBusy || this.state.agentText
            ?
              <Typography>{this.state.agentText && !this.audio.paused ? "Speaking..." : "Listening..."}</Typography>
            : <>
              <CircularProgress/>
              <Typography>Thinking...</Typography>
            </>}
          </StyledBoxRow>
        </StyledBoxColumn>
        <StyledBoxColumn style={{
          gap: PD_XLG,
          padding: PD_LG,
          alignItems: "center",
          pointerEvents: this.state.agentBusy ? "none" : "auto",
          filter: this.state.agentBusy ? "grayscale(1)" : null,
          opacity: this.state.agentBusy ? 0.5 : null,
        }}>
          <RecordAudioFragment
            disabled={this.state.agentBusy}
            autoStart={this.appPrefs.getAutoswitchCallTurn()}
            autoStop={this.appPrefs.getAutoswitchCallTurn()}
            displayMode={RecordAudioDisplayMode.EMBED}
            onDone={(url: string) => {
              this.respond(url);
            }}/>
        </StyledBoxColumn>
      </StyledBoxColumn>
      <StyledBoxRow style={{padding: PD_LG, alignItems: "center", justifyContent: "flex-end"}}>
        <IconButton
          onClick={() => {
            this.props.onBackPressed();
          }}
          style={{background: colorRed, color: "white"}}>
          <CallEndOutlined style={{width: 48, height: 48, padding: PD_SM}}/>
        </IconButton>
      </StyledBoxRow>
    </div>;
  }
}